import { useEffect, useState } from "react";
import { Moment } from "moment";
import { MiButton } from "@miplatform/components";
import { DATE_TIME_FORMATS } from "@miplatform/constants";
import { useAnchor } from "@miplatform/hooks";
import { ButtonSizes, ButtonVariants } from "@miplatform/types";
import { formatDate } from "@miplatform/utils";
import { mdiChevronDown, mdiClockOutline } from "@mdi/js";
import { Box, Divider, Menu, MenuItem, useTheme } from "@mui/material";
import { calculateAssignmentsAutoPublishRemainingTime } from "./AssignmentsCalendar.util";
import { AssignmentsStackedField } from "./AssignmentsStackedField";

interface AssignmentsPublishActionsProps {
  scheduledForTime: Moment;
  isAutoPublishOn?: boolean;
  onPublishNow?: () => void;
  onPauseAutoPublish?: () => void;
  onResumeAutoPublish?: () => void;
}

export const AssignmentsPublishActions = ({
  scheduledForTime,
  isAutoPublishOn = true,
  onPublishNow,
  onPauseAutoPublish,
  onResumeAutoPublish,
}: AssignmentsPublishActionsProps) => {
  const theme = useTheme();
  const [anchorEl, handleMenuOpen, handleMenuClose] = useAnchor();
  const isMenuOpen = Boolean(anchorEl);

  const handlePause = () => {
    handleMenuClose();
    onPauseAutoPublish?.();
  };

  const handleResume = () => {
    handleMenuClose();
    onResumeAutoPublish?.();
  };

  return (
    <Box display="flex" alignItems="center" gap={2}>
      <PublishCountDownTimer
        targetTime={scheduledForTime}
        isAutoPublishOn={isAutoPublishOn}
      />
      <Divider
        orientation="vertical"
        flexItem
        sx={{ height: 28, alignSelf: "center", mx: 2 }}
      />
      <AssignmentsStackedField title="Scheduled for">
        {isAutoPublishOn
          ? formatDate(scheduledForTime, DATE_TIME_FORMATS.FORMAT_12_HOUR)
          : "N/A"}
      </AssignmentsStackedField>
      <Box display="flex" alignItems="center" ml={2}>
        <MiButton
          title={isAutoPublishOn ? "Auto Publish On" : "Auto Publish Off"}
          size={ButtonSizes.Small}
          variant={ButtonVariants.Contained}
          onClick={handleMenuOpen}
          sx={{ padding: theme.spacing(0, 2), paddingLeft: theme.spacing(4) }}
          endIcon={{
            path: mdiChevronDown,
            iconColor: "#fff",
            iconSize: 0.8,
          }}
        >
          <Divider
            orientation="vertical"
            flexItem
            sx={{
              height: 30,
              width: "1px",
              backgroundColor: theme.palette.grey.accent,
              marginLeft: theme.spacing(1.5),
              marginRight: theme.spacing(0.5),
            }}
          />
        </MiButton>
        <Menu anchorEl={anchorEl} open={isMenuOpen} onClose={handleMenuClose}>
          <MenuItem
            onClick={handlePause}
            sx={{
              color: theme.palette.error.main,
              fontWeight: 600,
              fontSize: 14,
            }}
          >
            Pause Auto Publish
          </MenuItem>
          <Divider sx={{ my: 0.5 }} />
          <MenuItem
            onClick={handleResume}
            sx={{
              fontSize: 14,
            }}
          >
            Resume Auto Publish
          </MenuItem>
        </Menu>
      </Box>
      <MiButton
        size={ButtonSizes.Small}
        variant={ButtonVariants.Outlined}
        onClick={onPublishNow}
      >
        Publish Now
      </MiButton>
    </Box>
  );
};

interface PublishCountdownTimerProps {
  targetTime: Moment;
  isAutoPublishOn?: boolean;
}

const PublishCountDownTimer = ({
  targetTime,
  isAutoPublishOn = true,
}: PublishCountdownTimerProps) => {
  const [timeLeft, setTimeLeft] = useState<string>(() =>
    calculateAssignmentsAutoPublishRemainingTime(targetTime)
  );

  useEffect(() => {
    if (!isAutoPublishOn) return () => null;

    setTimeLeft(calculateAssignmentsAutoPublishRemainingTime(targetTime));

    const timer = setInterval(() => {
      setTimeLeft(calculateAssignmentsAutoPublishRemainingTime(targetTime));
    }, 1000);

    return () => clearInterval(timer);
  }, [targetTime, isAutoPublishOn]);

  return (
    <AssignmentsStackedField icon={mdiClockOutline} title="Publishes in">
      {isAutoPublishOn ? timeLeft : "N/A"}
    </AssignmentsStackedField>
  );
};
